import { useState, useMemo, Suspense, lazy, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Database, ChevronRight, ChevronDown } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn } from '@/shared/utils/utils';
import {
  MASTER_GROUPS,
  groupedRegistry,
  searchMasters,
  findMaster,
  groupLabel,
  type MasterRegistryEntry,
} from '../registry';
import { GenericMasterPage } from './GenericMasterPage';
import { SpecialMasterPage } from './SpecialMasterPage';

// ── Lazy loading fallback ────────────────────────────────────────────────────

function InlineLoader() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner />
    </div>
  );
}

// ── Custom/Form/Builder/Connection/Log/Toggles renderer ──────────────────────

function LazyComponentRenderer({ entry }: { entry: MasterRegistryEntry }) {
  const LazyComponent = useMemo(
    () => entry.component ? lazy(entry.component) : null,
    [entry.component],
  );

  if (!LazyComponent) return <p className="p-6 text-sm text-on-surface-variant">No editor configured for this entry.</p>;

  return (
    <Suspense fallback={<InlineLoader />}>
      <div className="h-full overflow-y-auto">
        <LazyComponent />
      </div>
    </Suspense>
  );
}

// ── Inline editor dispatcher ─────────────────────────────────────────────────

function MasterEditor({ entry }: { entry: MasterRegistryEntry }) {
  switch (entry.kind) {
    case 'generic':
      return <GenericMasterPage inlineKey={entry.masterType} />;
    case 'special':
      return <SpecialMasterPage inlineKind={entry.endpoint} />;
    case 'custom':
    case 'form':
    case 'builder':
    case 'connection':
    case 'log':
    case 'toggles':
      return <LazyComponentRenderer entry={entry} />;
    default:
      return null;
  }
}

// ── Main page ────────────────────────────────────────────────────────────────

export function MasterDataPage() {
  const { key: routeKey } = useParams<{ key: string }>();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(routeKey || null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const grouped = useMemo(() => groupedRegistry(), []);
  const searchResults = useMemo(() => searchMasters(searchQuery), [searchQuery]);
  const isSearching = searchQuery.trim().length > 0;

  const selectedEntry = selectedKey ? findMaster(selectedKey) : null;

  const selectMaster = useCallback(
    (key: string) => {
      setSelectedKey(key);
      const entry = findMaster(key);
      if (entry) {
        navigate(`/master-data/${entry.groupKey}/${key}`, { replace: true });
      }
    },
    [navigate],
  );

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  // Keyboard shortcut for search
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      const input = document.getElementById('master-data-search') as HTMLInputElement;
      input?.focus();
    }
  }, []);

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden" onKeyDown={handleKeyDown}>
      {/* ── Left sidebar ─────────────────────────────────────────────────── */}
      <aside className="w-72 shrink-0 border-r border-outline bg-surface-container-low overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-surface-container-low border-b border-outline px-4 py-3">
          <div className="flex items-center gap-2 mb-3">
            <Database className="h-5 w-5 text-primary" />
            <h1 className="text-sm font-bold text-on-surface tracking-tight">Master Data</h1>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-on-surface-variant" />
            <input
              id="master-data-search"
              type="text"
              placeholder="Search... (Ctrl+K)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-8 pl-8 pr-3 rounded border border-outline bg-surface text-body-sm text-on-surface placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        {/* Search results */}
        {isSearching ? (
          <div className="p-2">
            {searchResults.length === 0 ? (
              <p className="px-3 py-4 text-center text-body-sm text-on-surface-variant">
                No entries match "{searchQuery}"
              </p>
            ) : (
              searchResults.map((entry) => (
                <SidebarItem
                  key={entry.key}
                  entry={entry}
                  isSelected={selectedKey === entry.key}
                  onSelect={() => selectMaster(entry.key)}
                  showGroup
                />
              ))
            )}
          </div>
        ) : (
          /* Grouped list */
          <nav className="p-2 space-y-0.5">
            {MASTER_GROUPS.map((group) => {
              const entries = grouped.get(group.key) || [];
              if (entries.length === 0) return null;
              const isCollapsed = collapsedGroups.has(group.key);

              return (
                <div key={group.key}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="flex w-full items-center justify-between px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant hover:text-on-surface rounded hover:bg-surface-variant/40"
                  >
                    <span>{group.label}</span>
                    {isCollapsed
                      ? <ChevronRight className="h-3 w-3" />
                      : <ChevronDown className="h-3 w-3" />
                    }
                  </button>
                  {!isCollapsed && (
                    <div className="space-y-0.5 ml-1 pl-2 border-l border-outline/50">
                      {entries.map((entry) => (
                        <SidebarItem
                          key={entry.key}
                          entry={entry}
                          isSelected={selectedKey === entry.key}
                          onSelect={() => selectMaster(entry.key)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        )}
      </aside>

      {/* ── Right pane ───────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-hidden flex flex-col bg-surface">
        {/* Breadcrumb */}
        {selectedEntry && (
          <div className="shrink-0 border-b border-outline px-6 py-2 flex items-center gap-1.5 text-xs text-on-surface-variant">
            <button onClick={() => { setSelectedKey(null); navigate('/master-data'); }} className="hover:text-primary">Master Data</button>
            <ChevronRight className="h-3 w-3" />
            <span>{groupLabel(selectedEntry.groupKey)}</span>
            <ChevronRight className="h-3 w-3" />
            <span className="font-medium text-on-surface">{selectedEntry.label}</span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {selectedEntry ? (
            <MasterEditor entry={selectedEntry} />
          ) : (
            <WelcomePane />
          )}
        </div>
      </main>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function SidebarItem({
  entry,
  isSelected,
  onSelect,
  showGroup,
}: {
  entry: MasterRegistryEntry;
  isSelected: boolean;
  onSelect: () => void;
  showGroup?: boolean;
}) {
  const kindBadge = entry.kind === 'form' ? '⚙' : entry.kind === 'builder' ? '🎨' : entry.kind === 'connection' ? '🔗' : entry.kind === 'log' ? '📋' : entry.kind === 'toggles' ? '⚡' : '';

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-2 rounded px-3 py-1.5 text-left transition-colors',
        isSelected
          ? 'bg-primary/10 text-primary font-medium'
          : 'text-on-surface hover:bg-surface-variant/60',
      )}
    >
      <div className="min-w-0 flex-1">
        <span className="block text-body-sm truncate">
          {kindBadge && <span className="mr-1">{kindBadge}</span>}
          {entry.label}
        </span>
        {showGroup && (
          <span className="block text-[10px] text-on-surface-variant truncate">{entry.group}</span>
        )}
      </div>
    </button>
  );
}

function WelcomePane() {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="text-center max-w-md">
        <Database className="h-12 w-12 text-on-surface-variant/30 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-on-surface mb-2">Enterprise Configuration</h2>
        <p className="text-body-sm text-on-surface-variant mb-4">
          One screen to configure everything — masters, templates, integrations, company setup, and system settings. Use the tree or search (Ctrl+K) to find any entry.
        </p>
        <div className="text-xs text-on-surface-variant/70">
          {MASTER_GROUPS.length} groups · {MASTER_REGISTRY.length} entries
        </div>
      </div>
    </div>
  );
}

// Re-export for router compatibility
import { MASTER_REGISTRY } from '../registry';
